import { BoxProps } from '@mui/material';
import { forwardRef, ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { BoxStyled } from './PageStyles';
import { useLoadersCache } from '../../cache/common/loadersReactiveVar';
import icons from '../../constants/icons';
import { DEFAULT_PAGE_TITLE } from '../../constants/page';
import userAuth from '../../hooks/userAuth';
import { getCustomFavicon } from '../../utils/getCustomFavicon';
import { getCustomPageTitle } from '../../utils/getCustomPageTitle';
import { readWhiteLabelCache } from '../../utils/whiteLabelCache';
import { ROLE_ENUM } from '../../views/Auth/Login/interfaces/IGoogleUser';

interface IPageProps extends BoxProps {
  title?: string;
  children: ReactNode;
}

const Page = forwardRef<HTMLDivElement, IPageProps>(({ title, children, ...other }, ref): JSX.Element => {
  const { auth } = userAuth();
  const { loaders } = useLoadersCache();
  const { i18n } = useTranslation();
  const [showFavIcon, setShowFavIcon] = useState<boolean>(false);
  const customTheme = readWhiteLabelCache() ?? {};

  const getFavIcon = (): string => {
    if (customTheme.images?.favicon) {
      return getCustomFavicon(customTheme.images.favicon);
    }

    switch (auth?.userRole) {
      case ROLE_ENUM.ADMIN:
        return icons.FAVICON_ADVISOR;

      case ROLE_ENUM.STUDENT:
        return icons.FAVICON_LEARNER;

      default:
        return icons.FAVICON;
    }
  };

  useEffect(() => {
    if (loaders.length) {
      setShowFavIcon(false);
    } else {
      setShowFavIcon(true);
    }
  }, [loaders]);

  const pageTitle = getCustomPageTitle({
    defaultTitle: title ?? DEFAULT_PAGE_TITLE,
    userRole: auth?.userRole,
    currentLanguage: i18n.language,
  });

  return (
    <BoxStyled ref={ref} {...other}>
      <Helmet defer={false}>
        <title>{pageTitle}</title>
        {showFavIcon && <link rel="icon" type="image/x-icon" href={getFavIcon()} sizes="16x16" />}
        {!showFavIcon && <link rel="icon" type="image/x-icon" href={getFavIcon()} sizes="16x16" />}
      </Helmet>
      {children}
    </BoxStyled>
  );
});

export default Page;
